import { createFileRoute, Link } from "@tanstack/react-router";
import * as Tabs from "@radix-ui/react-tabs";
import * as Accordion from "@radix-ui/react-accordion";

import { loadAcademyList, loadAcademyNavigation } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";

export const Route = createFileRoute("/c1-academy/")({
  loader: async () => {
    const [lessons, categories] = await Promise.all([
      loadAcademyList(),
      loadAcademyNavigation(),
    ]);
    return { lessons, categories };
  },
  head: () => ({
    meta: seo({
      title: "C1 Academy",
      description: "Your hub for identity mastery. Step-by-step product guidance and best practices.",
      url: "/c1-academy",
    }),
    links: seoLinks({ url: "/c1-academy" }),
  }),
  component: AcademyIndex,
});

function AcademyIndex() {
  const { lessons, categories } = Route.useLoaderData();

  function slugFromPostUrl(url: string): string {
    return url
      .replace(/\.(md|mdx)$/, "")
      .replace(/^\/content\//, "")
      .replace(/^\/+/, "")
      .replace(/\/+$/, "")
      .split("/")
      .pop() ?? url;
  }

  const hasCategories = categories.length > 0;
  const defaultValue = categories[0]?.heading ?? "";

  function LessonGrid({ items }: { items: typeof lessons }) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {items.map((l) => (
          <Link
            key={l.slug}
            to="/c1-academy/$slug"
            params={{ slug: l.slug }}
            className="group overflow-hidden rounded-lg border border-[#DCDADA] transition-shadow hover:shadow-card"
          >
            {l.featured_image && (
              <div className="aspect-video overflow-hidden bg-gray-6">
                <img
                  src={l.featured_image}
                  alt={l.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
            <div className="p-5">
              <h2 className="mb-2 text-lg font-bold text-charcoal group-hover:text-teal-dark">{l.title}</h2>
              <p className="line-clamp-2 text-sm text-gray-60">{l.description_short}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-gray-60">
                {l.speaker && <span>{l.speaker}</span>}
                {l.length && <span>{l.length} min</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  }

  if (!hasCategories) {
    return (
      <div className="mx-auto max-w-screen-xl px-4 py-16 md:px-8">
        <h1 className="mb-2 font-display text-4xl font-bold text-charcoal">C1 Academy</h1>
        <p className="mb-12 text-lg text-gray-60">Your hub for identity mastery.</p>
        <LessonGrid items={lessons} />
      </div>
    );
  }

  function lessonsForCategory(posts: string[]) {
    // Keep the order the posts are listed in _index.md navigation.
    const bySlug = new Map(lessons.map((l) => [l.slug, l]));
    return posts
      .map((p) => bySlug.get(slugFromPostUrl(p)))
      .filter((l): l is (typeof lessons)[number] => l !== undefined);
  }

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-16 md:px-8">
      <h1 className="mb-2 font-display text-4xl font-bold text-charcoal">C1 Academy</h1>
      <p className="mb-6 text-lg text-gray-60">Your hub for identity mastery.</p>

      {/* Desktop: tabs */}
      <Tabs.Root defaultValue={defaultValue} className="hidden lg:flex lg:gap-16">
        <Tabs.List className="w-[16rem] space-y-2">
          {categories.map((cat) => (
            <Tabs.Trigger
              key={cat.heading}
              value={cat.heading}
              className="flex w-full items-center rounded-lg border border-transparent p-1.5 text-left font-body font-medium text-gray-80 hover:border-[#DCDADA] hover:bg-[#EBEBE9] data-[state=active]:border-teal-dark/30 data-[state=active]:bg-teal-dark/10 data-[state=active]:text-charcoal"
            >
              {cat.heading}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="w-full flex-1">
          {categories.map((cat) => (
            <Tabs.Content key={cat.heading} value={cat.heading}>
              <div className="rounded-20 bg-[#EBEBE9] px-3 py-4">
                <h3 className="mb-4 font-display text-xl font-medium text-charcoal">{cat.heading}</h3>
                <LessonGrid items={lessonsForCategory(cat.posts)} />
              </div>
            </Tabs.Content>
          ))}
        </div>
      </Tabs.Root>

      {/* Mobile: accordion */}
      <Accordion.Root type="single" collapsible className="mt-2 lg:hidden">
        {categories.map((cat) => (
          <Accordion.Item key={cat.heading} value={cat.heading} className="border-b border-[#DCDADA]">
            <Accordion.Header>
              <Accordion.Trigger className="group flex w-full items-center gap-2 py-3.5">
                <h3 className="flex-1 text-left font-body text-xl font-medium text-charcoal">{cat.heading}</h3>
                <svg
                  className="h-8 w-8 transition-transform group-data-[state=open]:rotate-180"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    d="M22.12 11.06 16 17.167 9.88 11.06 8 12.94 16 20.94l8-8-1.88-1.88Z"
                    fill="#00343a"
                  />
                </svg>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div className="mb-8 mt-2">
                <LessonGrid items={lessonsForCategory(cat.posts)} />
              </div>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </div>
  );
}
